// Module-level ref for bridging GoodsItemDetail (RHF) with the
// legacy AppRecord save flow.
//
// Holds a map of edited items keyed by "shipmentIndex:sequence" to support
// multiple goods shipments being edited in the same session.

import type { FieldErrors } from 'react-hook-form';
import type { GoodsItemDetailFormState } from '../schemas/forms/goodsItemDetail';
import type { GoodsItemApiData } from './GoodsItemDetailTransformer';

export interface GoodsItemEntry {
  formData: GoodsItemDetailFormState;
  original: GoodsItemApiData;
  /** If the item is currently mounted, this applies errors directly to the live form. */
  applyErrors?: (errors: GoodsItemValidationErrors) => void;
}

export type GoodsItemValidationErrors = FieldErrors<GoodsItemDetailFormState>;

const editedItems = new Map<string, GoodsItemEntry>();

const key = (shipmentIndex: number, sequence: number) =>
  `${shipmentIndex}:${sequence}`;

// Register or update an edited goods item.
export const setGoodsItemEntry = (
  shipmentIndex: number,
  sequence: number,
  entry: GoodsItemEntry,
) => {
  editedItems.set(key(shipmentIndex, sequence), entry);
};

// Remove a single entry (e.g. on discard/reset).
export const clearGoodsItemEntry = (
  shipmentIndex: number,
  sequence: number,
) => {
  editedItems.delete(key(shipmentIndex, sequence));
};

// Clear all entries (e.g. on page unmount).
export const clearAllGoodsItemEntries = () => {
  editedItems.clear();
};

// Get a single entry (e.g. for hydrating defaultValues on re-mount).
export const getGoodsItemEntry = (
  shipmentIndex: number,
  sequence: number,
): GoodsItemEntry | undefined => editedItems.get(key(shipmentIndex, sequence));

// Get edited items for a specific shipment, keyed by sequence.
export const getEditedGoodsItemsForShipment = (
  shipmentIndex: number,
): Map<number, GoodsItemEntry> => {
  const prefix = `${shipmentIndex}:`;
  const result = new Map<number, GoodsItemEntry>();
  for (const [k, v] of editedItems) {
    if (k.startsWith(prefix)) {
      const seq = Number(k.split(':')[1]);
      result.set(seq, v);
    }
  }
  return result;
};

// --- Validation errors for unmounted items ---
// Stored at save-time so they can be applied when the item re-mounts.
const pendingErrors = new Map<string, GoodsItemValidationErrors>();

// Store validation errors for an item (called from validateUpdate).
export const setGoodsItemErrors = (
  shipmentIndex: number,
  sequence: number,
  errors: GoodsItemValidationErrors,
) => {
  pendingErrors.set(key(shipmentIndex, sequence), errors);
};

// Get pending errors without clearing (for persistent display).
export const getGoodsItemErrors = (
  shipmentIndex: number,
  sequence: number,
): GoodsItemValidationErrors | undefined =>
  pendingErrors.get(key(shipmentIndex, sequence));

// Clear errors for a single item (e.g. when user fixes the field).
export const clearGoodsItemErrors = (
  shipmentIndex: number,
  sequence: number,
) => {
  pendingErrors.delete(key(shipmentIndex, sequence));
};

// Clear all pending errors (e.g. on successful save).
export const clearAllGoodsItemErrors = () => {
  pendingErrors.clear();
};
